import type { ReactNode } from 'react';

export type KpiTone = 'brand' | 'indigo' | 'success' | 'violet' | 'teal' | 'warning' | 'danger';

const TONE_MAP: Record<
  KpiTone,
  { tint: string; iconBg: string; halo: string; subtleText: string }
> = {
  brand: {
    tint: 'bg-brand-50/80 dark:bg-brand-900/20',
    iconBg: 'bg-gradient-to-br from-brand-500 to-brand-600',
    halo: 'bg-gradient-to-br from-brand-500 to-brand-700',
    subtleText: 'text-brand-700 dark:text-brand-300',
  },
  indigo: {
    tint: 'bg-indigo-50/80 dark:bg-indigo-900/20',
    iconBg: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
    halo: 'bg-gradient-to-br from-indigo-500 to-indigo-700',
    subtleText: 'text-indigo-700 dark:text-indigo-300',
  },
  success: {
    tint: 'bg-success-50/80 dark:bg-success-900/20',
    iconBg: 'bg-gradient-to-br from-success-500 to-success-600',
    halo: 'bg-gradient-to-br from-success-500 to-success-700',
    subtleText: 'text-success-700 dark:text-success-300',
  },
  violet: {
    tint: 'bg-violet-50/80 dark:bg-violet-900/20',
    iconBg: 'bg-gradient-to-br from-violet-500 to-violet-600',
    halo: 'bg-gradient-to-br from-violet-500 to-violet-700',
    subtleText: 'text-violet-700 dark:text-violet-300',
  },
  teal: {
    tint: 'bg-teal-50/80 dark:bg-teal-900/20',
    iconBg: 'bg-gradient-to-br from-teal-500 to-teal-600',
    halo: 'bg-gradient-to-br from-teal-500 to-teal-700',
    subtleText: 'text-teal-700 dark:text-teal-300',
  },
  warning: {
    tint: 'bg-warning-50/80 dark:bg-warning-900/20',
    iconBg: 'bg-gradient-to-br from-warning-500 to-warning-600',
    halo: 'bg-gradient-to-br from-warning-500 to-warning-700',
    subtleText: 'text-warning-700 dark:text-warning-300',
  },
  danger: {
    tint: 'bg-danger-50/80 dark:bg-danger-900/20',
    iconBg: 'bg-gradient-to-br from-danger-500 to-danger-600',
    halo: 'bg-gradient-to-br from-danger-500 to-danger-700',
    subtleText: 'text-danger-700 dark:text-danger-300',
  },
};

export interface KpiDelta {
  direction: 'up' | 'down' | 'flat';
  text: string;
  /** true = good (success green), false = bad (danger red). Default: up=good, down=bad. */
  positive?: boolean;
}

interface QuotationKpiProps {
  title: string;
  value: string;
  icon: ReactNode;
  tone: KpiTone;
  subtext?: string;
  subtextTone?: 'success' | 'warning' | 'danger' | 'neutral';
  delta?: KpiDelta;
  delay?: number;
}

/**
 * Quotation-domain KPI surface. Adheres to the three principles:
 *   • No Borders — uses shadow depth + light tint instead of border lines.
 *   • Mono Data  — value renders in JetBrains Mono via `font-mono tabular-nums`.
 *   • Glass Depth — backdrop-blur surface with layered shadow.
 *
 * Tone drives the icon gradient and tint, lending each metric its own
 * semantic colour without breaking the no-border rule.
 */
export function QuotationKpi({
  title,
  value,
  icon,
  tone,
  subtext,
  subtextTone = 'neutral',
  delta,
  delay,
}: QuotationKpiProps) {
  const t = TONE_MAP[tone];
  const subtextClass =
    subtextTone === 'success'
      ? 'text-success-700 dark:text-success-300'
      : subtextTone === 'warning'
        ? 'text-warning-700 dark:text-warning-300'
        : subtextTone === 'danger'
          ? 'text-danger-700 dark:text-danger-300'
          : 'text-neutral-500 dark:text-neutral-400';

  const deltaPositive =
    delta?.positive ?? (delta?.direction === 'up' ? true : delta?.direction === 'down' ? false : true);
  const deltaPillClass =
    delta?.direction === 'flat'
      ? 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800/60 dark:text-neutral-300'
      : deltaPositive
        ? 'bg-success-100/80 text-success-700 dark:bg-success-900/30 dark:text-success-300'
        : 'bg-danger-100/80 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300';
  const deltaArrow =
    delta?.direction === 'up' ? '↑' : delta?.direction === 'down' ? '↓' : '→';

  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${t.tint} backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)] hover:shadow-[0_18px_40px_-18px_rgba(15,23,42,0.28)] transition-all duration-200 p-5 animate-slide-up`}
      style={delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full ${t.halo} opacity-15`} aria-hidden="true" />
      <div className="relative flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${t.iconBg} flex items-center justify-center text-white shadow-[0_6px_16px_-8px_rgba(15,23,42,0.5)]`}>
          {icon}
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          {title}
        </span>
      </div>
      <div className="relative">
        <p className="font-mono text-2xl font-bold text-neutral-900 dark:text-white tabular-nums leading-none">
          {value}
        </p>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {delta && (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold font-mono tabular-nums ${deltaPillClass}`}
              aria-label={`Change versus previous period: ${delta.direction} ${delta.text}`}
            >
              <span className="leading-none">{deltaArrow}</span>
              <span>{delta.text}</span>
            </span>
          )}
          {subtext && (
            <p className={`text-xs font-medium tabular-nums ${subtextClass}`}>{subtext}</p>
          )}
        </div>
      </div>
    </div>
  );
}
